import {
  CommunicationSizeComponent,
  ComponentIds,
  ConnectorsComponent,
  Entity,
  HierarchyUtils,
  HostConnectionsComponent,
  Transform,
  TransformUtils
} from '@inter-ikea-core/core';
import { CoreApi, Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import * as R from 'ramda';

export const getPositionOfEntityTop = (entity: Entity, coreApi: CoreApi) => {
  const position = TransformUtils.worldPosition(entity);
  const height = coreApi.entities.getEntityComSize(entity)?.height || 0;
  return position.Y + height / 2;
};

const sortByWidth = (product1: Product, product2: Product) => {
  return product2.communicatedDimensions.width - product1.communicatedDimensions.width;
};

const sortByPosition = (entity1: Entity, entity2: Entity) => {
  const positionA = TransformUtils.worldPosition(entity1);
  const positionB = TransformUtils.worldPosition(entity2);
  return positionA.X === positionB.X ? positionA.Z - positionB.Z : positionA.X - positionB.X;
};

const hasStackedFrame = (frame: Entity) => {
  const activeHostConnections = frame.getComponent<HostConnectionsComponent>(
    ComponentIds.HOST_CONNECTIONS_COMPONENT_ID
  )?.connections;
  return activeHostConnections
    ? activeHostConnections.some(connection =>
        connection.hostConnector.tags().includes('stacking-host')
      )
    : false;
};

const isRegularFrame = (frame: Entity, coreApi: CoreApi) => {
  return coreApi.entities.isOneOfProductTypes(frame, [
    ProductType.BESTA_FRAME_600_ASM,
    ProductType.BESTA_FRAME_1200_ASM
  ]);
};

/**
 * Adds top panel to frame if it doesn't already have top
 */
const addTopPanelToFrame = (topPanel: Product, frame: Entity, coreApi: CoreApi) => {
  const stackingHostConnector = frame
    .getComponent<ConnectorsComponent>(ComponentIds.CONNECTORS_COMPONENT_ID)
    ?.connectors.find(c => c.tags().includes('stacking-host'));
  if (stackingHostConnector) {
    const position = stackingHostConnector.getWorldPositionFromOwnerEntity(frame);
    const topEntity = coreApi.getServices().productManager.createEntity(topPanel.coreProductId);
    if (!topEntity) return;
    TransformUtils.setWorldTransform(topEntity, new Transform(position));
    const result = coreApi.placementService.snapToTarget(topEntity, frame);
    coreApi.entities.disposeCollidingEntities(result.errors);
    return result;
  }
};

/**
 * Calculate the best combination of top panels (fewest) that covers the complete frame group width
 */
const findBestTopCombination = (topPanels: Product[], frameGroupWidth: number) => {
  let shortestCombination: number[] | null = null;
  const availableWidths = topPanels.map(panel => panel.communicatedDimensions.width);

  const find = (currentCombination: number[], currentSum: number, startIndex: number) => {
    if (currentSum === frameGroupWidth) {
      // Check if this combination is shorter than the current shortest
      if (shortestCombination === null || currentCombination.length < shortestCombination.length) {
        shortestCombination = [...currentCombination];
      } else if (
        shortestCombination.length === currentCombination.length &&
        R.uniq(currentCombination).length < R.uniq(shortestCombination).length
      ) {
        shortestCombination = [...currentCombination];
      }
      return;
    }

    // If the current sum exceeds the total width, stop exploring this path
    if (currentSum > frameGroupWidth) {
      return;
    }

    // Iterate over the available widths, starting from the last used index
    for (let i = startIndex; i < availableWidths.length; i++) {
      currentCombination.push(availableWidths[i]);
      find(currentCombination, currentSum + availableWidths[i], i); // Allow reuse of elements
      currentCombination.pop();
    }
  };

  find([], 0, 0);

  return shortestCombination
    ? ((shortestCombination as number[])
        .map(width => topPanels.find(panel => panel.communicatedDimensions.width === width))
        .filter(Boolean) as Product[])
    : [];
};

/**
 * Get top panel on specified frame
 */
export const getTopPanelForFrame = (frame: Entity, coreApi: CoreApi) => {
  const childrenTop = HierarchyUtils.descendants(frame).find(child =>
    coreApi.entities.isOneOfProductTypes(child, [
      ProductType.BESTA_TOP_PANEL,
      ProductType.BESTA_TOP_PANEL_TV_BENCH
    ])
  );
  if (childrenTop) {
    return childrenTop;
  }
  // If frames are in automatic entity group the hierachy changes so then we check HostConnectionsComponent instead
  const activeHostConnections = frame.getComponent<HostConnectionsComponent>(
    ComponentIds.HOST_CONNECTIONS_COMPONENT_ID
  )?.connections;
  if (!activeHostConnections || activeHostConnections.length === 0) {
    return null;
  }
  const topConnection = activeHostConnections.find(connection =>
    coreApi.entities.isProductType(connection.guestConnectorEntity, ProductType.BESTA_TOP_PANEL)
  );
  return topConnection ? topConnection.guestConnectorEntity : null;
};

/**
 * Get the total width of frames in group
 */
export const getTotalWidth = (entities: Entity[], coreApi: CoreApi) => {
  return entities.reduce((width: number, frame: Entity) => {
    const frameWidth = coreApi.entities.getEntityComSize(frame)?.width || 0;
    return width + frameWidth;
  }, 0);
};

/**
 * Returns all groups of frames in scene that can have top panels (frames below 150cm)
 */
export const getAllFrameGroupsForTops = (coreApi: CoreApi): Entity[][] => {
  const framesInScene = coreApi.entities
    .getByProductTypes([ProductType.BESTA_FRAME_600_ASM, ProductType.BESTA_FRAME_1200_ASM])
    .filter(frame => getPositionOfEntityTop(frame, coreApi) < 1500); // Don't add tops to frames above 150cm
  const groups: Entity[][] = [];
  framesInScene.forEach(frame => {
    if (!groups.some(group => group.includes(frame)) && !hasStackedFrame(frame)) {
      groups.push(getFrameGroupForTop(frame, coreApi));
    }
  });
  return groups;
};

/**
 * Returns all aligned frames that can have top panesl for a given frame
 */
export const getFrameGroupForTop = (frame: Entity, coreApi: CoreApi): Entity[] => {
  if (hasStackedFrame(frame)) {
    return [];
  }
  const group = [frame];
  const frameNeighbors = coreApi.entities.getAlignedFrames(
    frame,
    'align-frame-top-left',
    'align-frame-top-right',
    false,
    true
  );
  let leftNeigbor = frameNeighbors.left;
  while (leftNeigbor && !hasStackedFrame(leftNeigbor) && isRegularFrame(leftNeigbor, coreApi)) {
    group.push(leftNeigbor);
    leftNeigbor = coreApi.entities.getAlignedFrames(
      leftNeigbor,
      'align-frame-top-left',
      'align-frame-top-right',
      false,
      true
    ).left;
  }
  let rightNeighbour = frameNeighbors.right;
  while (
    rightNeighbour &&
    !hasStackedFrame(rightNeighbour) &&
    isRegularFrame(rightNeighbour, coreApi)
  ) {
    group.push(rightNeighbour);
    rightNeighbour = coreApi.entities.getAlignedFrames(
      rightNeighbour,
      'align-frame-top-left',
      'align-frame-top-right',
      true,
      true
    ).right;
  }
  return group;
};

/**
 * Removes current top panels and adds new to group of aligned frames
 */
export const addTopPanelsToFrameGroup = (
  group: Entity[],
  topPanelProducts: Product[],
  coreApi: CoreApi
) => {
  const sortedTopPanels = topPanelProducts
    .sort(sortByWidth)
    .filter(
      top =>
        top.communicatedDimensions.depth ===
        group[0]?.getComponent<CommunicationSizeComponent>(
          ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
        )?.size.depth
    );
  const frameGroupWidth = getTotalWidth(group, coreApi);
  const frames = group.sort(sortByPosition);
  const currentTops = frames
    .map(frame => getTopPanelForFrame(frame, coreApi))
    .filter(Boolean) as Entity[];
  coreApi.entities.deleteMultiple(currentTops);
  // Find best combination of top panels for frame group (fewest tops)
  const topCombination = findBestTopCombination(sortedTopPanels, frameGroupWidth);
  topCombination.forEach(top => {
    for (const frame of frames) {
      const result = addTopPanelToFrame(top, frame, coreApi);
      if (result?.valid) {
        break;
      }
    }
  });
};

/**
 * Returns true if selected frame has top panel
 */
export const selectedFrameHasAnyTopPanel = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity || !selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    return false;
  }
  return getTopPanelForFrame(selectedEntity, coreApi) !== null;
};

/**
 * Returns true if selected frame has specified top panel
 */
export const selectedFrameHasTopPanel = (top: Product, coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity || !selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    return false;
  }
  return frameHasTopPanelDesign(selectedEntity, top.design, coreApi);
};

/**
 * Returns true if frame has specified top panel design
 */
export const frameHasTopPanelDesign = (frame: Entity, design: string, coreApi: CoreApi) => {
  const topOnFrame = getTopPanelForFrame(frame, coreApi);
  return topOnFrame
    ? topOnFrame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design === design
    : false;
};
